import {effectiveUtmParams} from '@app/modules/shared/validators/tracked-url.validator';
import {SbCtaUrlInputComponent} from '@app/_components/unified/sb-url-input/sb-cta-url-input.component';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ChangeDetectionStrategy, effect, inject, output, signal, viewChild, input, linkedSignal, untracked } from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {User, Influencer, InfluencerType, Project} from '@app/modules/shared/models';
import {ActivatedRoute, Router} from '@angular/router';
import { AbstractControl, FormGroup, FormArray, FormBuilder, Validators, FormControl, ReactiveFormsModule } from '@angular/forms';
import {Constants} from '@app/consts';
import {select, Store} from '@ngrx/store';
import {
  getInfluencer,
  getInfluencerError,
  getInfluencersProjectSegment,
  getInfluencersTypes,
  getInfluencerSuccess,
  getLoggedInUser,
  getProject,
  getProjectError,
  getVariableError,
  getVariables,
  IAuthenticationState,
  IInfluencersState,
  InfluencersGet,
  InfluencersProjectSegment,
  InfluencersTypeList,
  InfluencersUpdate,
  IProjectState,
  IVariableState,
  ProjectGet,
  VariableList,
  UsersUpdate
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs/operators';
import {CreatableSelectComponent} from '@app/_components/creatable-select';
import {FormService} from '@app/services/form.service';
import Swal from 'sweetalert2';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbInputImageComponent } from '../../../_components/unified/sb-input/sb-input-image.component';
import { SbInputTimeComponent } from '../../../_components/unified/sb-input/sb-input-time.component';
import { CreatableSelectComponent as CreatableSelectComponent_1 } from '../../../_components/creatable-select/creatable-select.component';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';

@Component({
  selector: 'app-influencer-view',
  templateUrl: './influencer-view.component.html',
  styleUrls: ['./influencer-view.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbCtaUrlInputComponent, PageShellComponent, ReactiveFormsModule, SbInputImageComponent, SbInputTimeComponent, CreatableSelectComponent_1, InfoPopoverComponent]
})
export class InfluencerViewComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private influencersStore = inject<Store<IInfluencersState>>(Store);
  private variableStore = inject<Store<IVariableState>>(Store);
  private projectStore = inject<Store<IProjectState>>(Store);
  private toastr = inject(ToastrService);
  private constants = inject(Constants);
  formService = inject(FormService);
  private elRef = inject(ElementRef);

  readonly hideClass = input(false);
  currentUser: User;
  influencer: Influencer = null;
  addSocialClick = true;
  currentUserSubscription: Subscription;
  /** Built from the influencer's type: its controls change with the type. */
  viewInfluencerForm: FormGroup<{[key: string]: AbstractControl<any>}>;
  submitted = false;
  segmentsGlobal: any = [];
  readonly segments = signal<any>([]);
  urlRegex = '';
  loaded = false;
  showForm = false;
  selectedIndex = false;
  imageProfile = '../../assets/images/blue-logo.png';
  influencerTypes: InfluencerType[] = [];
  unsubscriber = new Subject();
  readonly projectIdInput = input<number>(undefined, {alias: 'projectId'});
  readonly projectId = linkedSignal(() => this.projectIdInput());
  readonly influencerIdInput = input<number>(undefined, {alias: 'influencerId'});
  readonly influencerId = linkedSignal(() => this.influencerIdInput());
  readonly isModal = input(false);
  readonly closeModal = output<any>();
  readonly segmentSelect = viewChild<CreatableSelectComponent>('segmentSelect');
  public controls: any = {};
  public variables = {};
  project: Project;
  deleteAction = false;
  skipValues: any[] = [];
  selectedSkip = null;

  campaignId;
  influencerType;
  selectedSegment;
  get f() {
    return this.viewInfluencerForm.controls;
  }

  get teamsGroup() {
    return this.viewInfluencerForm.get('teams') as FormArray;
  }

  get socialsGroup() {
    return this.viewInfluencerForm.get('socials') as FormArray;
  }

  get sessionArray() {
    return this.viewInfluencerForm.get('sessions').value;
  }

  get sessionsGroup() {
    return this.viewInfluencerForm.get('sessions') as FormArray;
  }

  constructor() {
    const constants = this.constants;

    this.skipValues = constants.skipValues;
    this.viewInfluencerForm = this.formBuilder.group<{[key: string]: AbstractControl<any>}>({});
    this.route.params.subscribe(params => {
      if (params.projectId && params.influencerId) {
        this.projectId.set(+params.projectId);
        this.influencerId.set(+params.influencerId);
        const projectId = this.projectId();
        this.projectStore.dispatch(ProjectGet({projectId: projectId}));
        this.influencersStore.dispatch(InfluencersProjectSegment({projectId: projectId}));
        this.influencersStore.dispatch(InfluencersGet({influencerId: this.influencerId()}));
      }
    });
    this.influencersStore.dispatch(InfluencersTypeList({params: {influencer_fields: true}}));
    if(this.route.snapshot.queryParams?.selectedInfluencerType) {
      this.influencerType = this.route.snapshot.queryParams?.selectedInfluencerType;
    }
    if(this.route.snapshot.queryParams?.campaignId) {
      this.campaignId = this.route.snapshot.queryParams?.campaignId;
    }
    if(this.route.snapshot.queryParams?.selectedSegment){
      this.selectedSegment = Number(this.route.snapshot.queryParams?.selectedSegment);
    }
    this.subscribeStore();
    // A page that shows the influencer in place hands the ids in: each new one loads its data.
    effect(() => {
      const projectId = this.projectIdInput();
      if (projectId) {
        untracked(() => {
          this.projectStore.dispatch(ProjectGet({projectId}));
          this.influencersStore.dispatch(InfluencersProjectSegment({projectId}));
        });
      }
    });
    effect(() => {
      const influencerId = this.influencerIdInput();
      if (influencerId) {
        untracked(() => this.influencersStore.dispatch(InfluencersGet({influencerId})));
      }
    });
  }

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        this.currentUser = user;
      }
      );

    this.influencersStore.pipe(select(getInfluencersProjectSegment))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(segments => {
        if (segments && segments.length > 0) {
          this.segmentsGlobal = segments;
          this.segments.set(this.segmentsGlobal);
        }
      });
    this.influencersStore.pipe(select(getInfluencerSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.submitted) {
            if (this.campaignId) {
              this.router.navigate([`/project/${this.projectId()}/add-influencer`],
                { queryParams: { campId: this.campaignId , selectedSegmentUrl: this.selectedSegment , selectedInfluencerUrl : this.influencerType } });
            } else {
              this.router.navigate([`/projects/${this.projectId()}/influencers`]);
            }
          }
        }
      }
      );
    this.influencersStore.pipe(select(getInfluencer))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencer => {
        if (influencer) {
          this.influencer = influencer;
          this.patchValues();
          // The form is rebuilt for the influencer's type and patched.
          this.cdr.markForCheck();
        }
      }
      );
    this.influencersStore.pipe(select(getInfluencerError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      }
      );
    this.influencersStore.pipe(select(getInfluencersTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencerTypes => {
        if (influencerTypes && influencerTypes.length > 0) {
          this.influencerTypes = influencerTypes;
          this.addSocialClick = true;
          this.patchValues();
          // The types' choices, and the form rebuilt for the influencer's type.
          this.cdr.markForCheck();
        }
      });
    this.variableStoreSubscribe();

    this.projectStore.pipe(select(getProjectError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    this.projectStore.pipe(select(getProject))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(project => {
        if (project) {
          this.project = project;
        }
      });
  }

  variableStoreSubscribe() {
    this.variableStore.pipe(select(getVariableError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    this.variableStore.pipe(select(getVariables))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(variables => {
        if (variables) {
          const result = [];
          Object.entries(variables).forEach(([key, value]) => {
            if (value.hasOwnProperty('group') && value['group']) {
              if (!result[value['group']]) {
                result[value['group']] = [];
              }
              result[value['group']].push({
                name: value['title'].replace(value['group'].toString().toLowerCase().trim(), ''),
                key: value['alias'] ? `#${value['alias']}#` : value['key'],
                valueType: value['valueType']
              });
            } else {
              if (!result['generic']) {
                result['generic'] = [];
              }
              result['generic'].push({
                name: value['title'],
                key: value['key'],
                valueType: value['valueType']
              });
            }
          });
          this.variables = result;
        }
      });
  }

  patchValues() {
    if (this.influencer && this.influencerTypes && this.influencerTypes.length > 0) {
      this.createForm(this.influencer.influencer_type_id);
      this.filterSegments(false);
      const values: any = {};
      Object.keys(this.controls).forEach(key => {
        if (key === 'sessions') {
          values[key] = [];
          if (this.influencer.sessions && Array.isArray(this.influencer.sessions)) {
            const arr = this.influencer.sessions.map(item => this.addSessionFormGroup(item));
            this.viewInfluencerForm.setControl('sessions', new FormArray(arr));
          } else {
            this.viewInfluencerForm.setControl('sessions', new FormArray([this.addSessionFormGroup()]));
          }
        } else {
          values[key] = this.influencer[key];
        }
      });
      this.viewInfluencerForm.patchValue(values);
      this.formService.setNewOrExistingSegmentValidation(this.viewInfluencerForm, 1);
    }
  }

  filterSegments(resetFields = true) {
    if (resetFields) {
      this.viewInfluencerForm.patchValue({project_influencer_segment_new_name: '', project_influencer_segment_id: ''});
    }
    const influencerType = this.viewInfluencerForm.get('influencer_type_id').value;
    if (influencerType) {
      const segmentSelect = this.segmentSelect();
      if (segmentSelect) {
        segmentSelect.clearSelection();
      }
      this.segments.set(this.segmentsGlobal.filter(
        p => p.influencer_type_id === Number(this.viewInfluencerForm.get('influencer_type_id').value)
      ));
    }
  }

  createForm(influencerType: number) {

    const validators = {
      first_name: [Validators.required],
      name: [Validators.required],
      influencer_type_id: [Validators.required],
      project_influencer_segment_id: [Validators.required],
      last_name: [Validators.required],
      email: [Validators.required, Validators.email],
      email_alias: [Validators.email],
      registration_url: [Validators.pattern(this.constants.urlRegex)],
      video_url: [],
      skip:[],
      prefix: [],
      suffix: [],
    };
    this.controls = {};

    Object.keys(this.viewInfluencerForm.controls).forEach(key => {
      this.viewInfluencerForm.removeControl(key);
    });
    influencerType = Number(influencerType);
    this.showForm = false;
    this.viewInfluencerForm.addControl('project_influencer_segment_new_name', new FormControl(''));
    this.viewInfluencerForm.addControl('skip', new FormControl());
    this.viewInfluencerForm.addControl('email_source_message', new FormControl());
    this.viewInfluencerForm.addControl('custom_message', new FormControl());
    // this.addInfluencerForm.addControl('facebook', new FormControl('', [Validators.pattern(this.constants.urlRegex)]));
    // this.addInfluencerForm.addControl('twitter', new FormControl('', [Validators.pattern(this.constants.urlRegex)]));
    // this.addInfluencerForm.addControl('linkedin', new FormControl('', [Validators.pattern(this.constants.urlRegex)]));
    // this.addInfluencerForm.addControl('instagram', new FormControl('', [Validators.pattern(this.constants.urlRegex)]));
    // this.addInfluencerForm.addControl('socials', new FormArray([]));
    // this.addInfluencerForm.addControl('teams', new FormArray([this.addTeamsFormGroup()]));

    const influencer = this.influencerTypes.find(item => item.id === influencerType);
    const fieldsObject = influencer.fields;
    Object.keys(fieldsObject).forEach(key => {
      if (fieldsObject[key].type === 'all') {
        if (fieldsObject[key].editable) {
          this.addControls(fieldsObject, key, validators);
        }
      } else {
        if (fieldsObject[key].type.includes(influencer.slug)) {
          if (fieldsObject[key].editable) {
            this.addControls(fieldsObject, key, validators);
          }
        }
      }
    });
    this.viewInfluencerForm.patchValue({influencer_type_id: influencerType});
    this.selectedSkip = this.skipValues.find(x => x.value ===  this.influencer['skip'] || null);
    this.viewInfluencerForm.patchValue({skip: this.selectedSkip?.value || null});
    if (this.influencer?.is_generic) {
      this.viewInfluencerForm.get('email')?.disable();
    }
    this.showForm = true;
  }

  addControls(fieldsObject, key, validators) {
    this.controls[key] = key;
    if (key === 'sessions') {
      const arr = [];

      if (this.influencer?.sessions?.length) {
        this.influencer.sessions.forEach(item => {
          arr.push(this.addSessionFormGroup(item));
        });
      } else {
        arr.push(this.addSessionFormGroup());
      }

      this.viewInfluencerForm.addControl('sessions', new FormArray(arr));
    } else {
      this.viewInfluencerForm.addControl(key, new FormControl('', validators[key] || []));
    }
  }

  segmentSelected(event: any) {
    if (event) {
      if (event.isNew) {
        this.viewInfluencerForm.patchValue({project_influencer_segment_new_name: event.value});
        this.formService.setNewOrExistingSegmentValidation(this.viewInfluencerForm, 0);
      } else {
        this.viewInfluencerForm.patchValue({project_influencer_segment_id: event.value});
        this.formService.setNewOrExistingSegmentValidation(this.viewInfluencerForm, 1);
      }
    } else {
      this.viewInfluencerForm.patchValue({project_influencer_segment_new_name: '', project_influencer_segment_id: ''});
      this.formService.setNewOrExistingSegmentValidation(this.viewInfluencerForm, 0);
    }
  }

  skipSelected(event: any) {
    if(event?.value){
      this.selectedSkip = this.skipValues.find(x => x.id === event.value || null);
      this.viewInfluencerForm.patchValue({skip: this.selectedSkip.value});
    } else {
      this.viewInfluencerForm.patchValue({skip: null});
    }
  }

  /** What the tracking link adds to a contact's own call to action, as far as the form knows. */
  callToActionUtmParams(): string | null {
    return effectiveUtmParams(this.project?.google_utm_params, this.project?.client);
  }

  ngOnInit() {
    this.variableStore.dispatch(VariableList({params: {}}));
  }

  addTeamsFormGroup() {
    return this.formBuilder.group({
      profile_img: [''],
      prefix: [''],
      suffix: [''],
      first_name: [''],
      last_name: [''],
      email: [''],
      phone: [''],
      job_title: [''],
      skip: ['']
    });
  }

  addSocialsFormGroup() {
    return this.formBuilder.group({
      social_label: [''],
      social_value: ['', [Validators.pattern(this.constants.urlRegex)]]
    });
  }

  addSessionFormGroup(data: any = null) {
    return this.formBuilder.group({
      title: [data?.title || ''],
      description: [data?.description || ''],
      start_time: [data?.start_time || ''],
      end_time: [data?.end_time || '']
    });
  }

  onProfileChanged(event, index) {
    if (event.target.files && event.target.files.length) {
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      }).then(data => {
        this.teamsGroup.at(index).patchValue({profile_img: data});
        this.cdr.markForCheck();
      });
    }
  }

  addTeamsButtonClick(): void {
    this.teamsGroup.push(this.addTeamsFormGroup());
  }

  addSessionButtonClick(): void {
    this.sessionsGroup.push(this.addSessionFormGroup());
  }

  addSocialsButtonClick(): void {
    this.socialsGroup.push(this.addSocialsFormGroup());
  }

  removeTeamsButtonClick(index: number): void {
    this.teamsGroup.removeAt(index);
  }

  removeSocialsButtonClick(index: number): void {
    this.socialsGroup.removeAt(index);
  }

  removeSessionButtonClick(index: number): void {
    this.sessionsGroup.removeAt(index);
  }

  updateInfluencer() {
    this.submitted = true;
    if (this.viewInfluencerForm.invalid) {
      setTimeout(() => {
        this.scrollToField();
      }, 500);
      return;
    }

    this.updateInfluencerData();
  }

  updateInfluencerData() {
    const body: any = this.viewInfluencerForm.value;
    if (body.hasOwnProperty('sessions')) {
      body.sessions = body.sessions.map(s => ({
        title: s.title || '',
        description: s.description || '',
        start_time: s.start_time || null,
        end_time: s.end_time || null
      }));
    // if (body.hasOwnProperty('sessions')) {
    //   body.sessions.forEach(item => {
    //     sessions.push(item.session);
    //   });
    }
    // body.sessions = sessions.filter((item) => item).join('|');

    this.influencersStore.dispatch(InfluencersUpdate({
      influencerId: this.influencerId(),
      influencer: {
        project_id: this.projectId(),
        skipped_by: this.viewInfluencerForm.value.skip ? this.currentUser.id : null,
        ...body
      }
    }));
  }

  updateValue(variable, textFor) {
    const value = this.viewInfluencerForm.get(textFor).value;
    const varValue = variable.key;
    this.viewInfluencerForm.controls[textFor].setValue(`${value} ${varValue}`);
  }

  getImageType() {
    const influencersType = this.viewInfluencerForm.get('influencer_type_id').value;
    const type = this.influencerTypes.find(item => item.id === influencersType);
    if (type) {
      if (type.slug === 'attendee' || type.slug === 'speaker') {
        return 'Headshot';
      } else if (type.slug === 'partner') {
        return 'Logo';
      }
    }
    return 'Logo or Headshot';
  }

  scrollToField() {
    const invalid = this.elRef.nativeElement.querySelector('.invalid-text');
    if (invalid) {
      this.toastr.warning('Some fields have error, please review', 'warning');
      const doc = this.isModal() ? document.querySelector('.custom-modal .modal-dialog .modal-body') : window;
      doc.scrollBy({
        top: invalid.getBoundingClientRect().top - 150,
        left: 0,
        behavior: 'smooth'
      });
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
